/**
 * Compact wallet control for the top bar — "Connect wallet" when empty, and a
 * dropdown with the active address, chain, a link to the full Wallet page and a
 * disconnect when linked. Built on `@authe/web3` hooks so it shares connection
 * state with the rest of the app (this is a blockchain product — the wallet is
 * always one click away).
 */
import { Link } from "@tanstack/react-router";
import { ChevronDown, LogOut, Wallet as WalletIcon } from "lucide-react";
import { useWalletConnection, useAddressInventory } from "@authe/web3";
import {
  AddressDisplay,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@authe/ui-core";
import { ROUTES } from "@/config";

export function WalletButton(): React.ReactElement {
  const { isConnected, isConnecting, connect, disconnect } = useWalletConnection();
  const { entries, chain } = useAddressInventory();
  const active = entries.find((e) => e.isActive) ?? entries[0];

  if (!isConnected || !active) {
    return (
      // Keep clickable while connecting so a dismissed wallet prompt that leaves
      // the provider promise hanging can be retried immediately.
      <Button size="sm" variant="outline" aria-label={isConnecting ? "Connecting wallet" : "Connect wallet"} onClick={() => connect()}>
        <WalletIcon className="h-4 w-4" />
        <span className="hidden sm:inline">{isConnecting ? "Connecting…" : "Connect wallet"}</span>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="gap-2" aria-label={`Wallet ${active.address}`}>
          <WalletIcon className="h-4 w-4" />
          <span className="hidden font-mono sm:inline"><AddressDisplay address={active.address} interactive={false} /></span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="font-mono text-xs"><AddressDisplay address={active.address} interactive={false} /></div>
          {chain?.name && (
            <div className="text-xs font-normal text-muted-foreground">{chain.name}</div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={ROUTES.wallet}>
            <WalletIcon className="h-4 w-4" />
            Open wallet
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void disconnect()}>
          <LogOut className="h-4 w-4" />
          Disconnect
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
